export interface ParsedGoal {
  goal_type: 'strength' | 'endurance' | 'weight' | 'performance';
  exercise_name: string;
  target_value: number;
  target_unit: string;
  current_value: number;
  description: string;
}

export const parseGoalsFromText = (text: string): ParsedGoal[] => {
  if (!text?.trim()) return [];

  const goals: ParsedGoal[] = [];
  const sentences = text.split(/[.!;]/).map(s => s.trim()).filter(s => s.length > 0);

  for (const sentence of sentences) {
    const lowerSentence = sentence.toLowerCase();
    
    // Strength goals - bench press, squat, deadlift, etc.
    const strengthMatch = lowerSentence.match(/(bench\s*press|squat|deadlift|press|lift|row).*?(\d+)\s*(kg|lbs|pounds)/);
    if (strengthMatch) {
      const exercise = strengthMatch[1];
      const value = parseInt(strengthMatch[2]);
      const unit = strengthMatch[3] === 'pounds' ? 'lbs' : strengthMatch[3];
      
      goals.push({
        goal_type: 'strength',
        exercise_name: exercise.charAt(0).toUpperCase() + exercise.slice(1).replace(/\s+/g, ' '),
        target_value: value,
        target_unit: unit,
        current_value: 0,
        description: sentence
      });
      continue;
    }

    // Weight goals
    const weightLossMatch = lowerSentence.match(/(lose|drop).*?(\d+)\s*(kg|lbs|pounds)/);
    const weightGainMatch = lowerSentence.match(/(gain|put on).*?(\d+)\s*(kg|lbs|pounds)/);
    
    if (weightLossMatch) {
      const value = parseInt(weightLossMatch[2]);
      const unit = weightLossMatch[3] === 'pounds' ? 'lbs' : weightLossMatch[3];
      
      goals.push({
        goal_type: 'weight',
        exercise_name: 'Weight Loss',
        target_value: value,
        target_unit: unit,
        current_value: 0,
        description: sentence
      });
      continue;
    }
    
    if (weightGainMatch) {
      const value = parseInt(weightGainMatch[2]);
      const unit = weightGainMatch[3] === 'pounds' ? 'lbs' : weightGainMatch[3];
      
      goals.push({
        goal_type: 'weight',
        exercise_name: 'Weight Gain',
        target_value: value,
        target_unit: unit,
        current_value: 0,
        description: sentence
      });
      continue;
    }

    // Running/Endurance goals
    const runningTimeMatch = lowerSentence.match(/(run|marathon|half marathon|5k|10k).*?(\d+):(\d+)|(\d+)\s*minutes?/);
    const runningDistanceMatch = lowerSentence.match(/(run|jog).*?(\d+)\s*(km|miles?|kilometers?)/);
    
    if (runningTimeMatch) {
      let minutes = 0;
      if (runningTimeMatch[2] && runningTimeMatch[3]) {
        // Format like "4:30" (hours:minutes)
        minutes = parseInt(runningTimeMatch[2]) * 60 + parseInt(runningTimeMatch[3]);
      } else if (runningTimeMatch[4]) {
        // Format like "30 minutes"
        minutes = parseInt(runningTimeMatch[4]);
      }
      
      if (minutes > 0) {
        const eventType = runningTimeMatch[1];
        goals.push({
          goal_type: 'endurance',
          exercise_name: eventType.charAt(0).toUpperCase() + eventType.slice(1),
          target_value: minutes,
          target_unit: 'minutes',
          current_value: 0,
          description: sentence
        });
        continue;
      }
    }
    
    if (runningDistanceMatch) {
      const value = parseInt(runningDistanceMatch[2]);
      const unit = runningDistanceMatch[3].includes('mile') ? 'miles' : 'km';
      
      goals.push({
        goal_type: 'endurance',
        exercise_name: 'Running Distance',
        target_value: value,
        target_unit: unit,
        current_value: 0,
        description: sentence
      });
      continue;
    }

    // Body fat percentage
    const bodyFatMatch = lowerSentence.match(/(\d+)%?\s*(body\s*fat|bf)/);
    if (bodyFatMatch) {
      const value = parseInt(bodyFatMatch[1]);
      
      goals.push({
        goal_type: 'performance',
        exercise_name: 'Body Fat Percentage',
        target_value: value,
        target_unit: '%',
        current_value: 0,
        description: sentence
      });
      continue;
    }

    // Muscle building / strength related without specific numbers
    if (lowerSentence.match(/(build|gain|increase|develop)\s*(muscle|strength|size|mass)/)) {
      goals.push({
        goal_type: 'performance',
        exercise_name: 'Muscle Building',
        target_value: 100,
        target_unit: 'progress',
        current_value: 0,
        description: sentence
      });
      continue;
    }

    // General fitness / health goals
    if (lowerSentence.match(/(get|become|stay)\s*(fit|healthy|stronger|leaner|athletic)/)) {
      goals.push({
        goal_type: 'performance',
        exercise_name: 'General Fitness',
        target_value: 100,
        target_unit: 'progress',
        current_value: 0,
        description: sentence
      });
      continue;
    }
  }

  return goals;
};